import React, { lazy, Suspense, useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import config from './config'; // Import the config file
import Header from './components/Header'; // Import the Header component
import Footer from './components/Footer'; // Import the Footer component
import { scheduleAutoLogout } from './utils/auth'; // Import the scheduleAutoLogout function
//import './App.css'; // Ensure you have styles for light and dark themes
import llStorage from './llStorage';
import { t } from './i18n'; // Add this import
import axiosInstance from './axiosInstance';

const About = lazy(() => import('./About'));
const ApiKeyCard = lazy(() => import('./ApiKeyCard'));
const ApiKeys = lazy(() => import('./ApiKeys'));
const AdminSkus = lazy(() => import('./AdminSkus'));
const Dashboard = lazy(() => import('./Dashboard'));
const EditItem = lazy(() => import('./EditItem'));
const Files = lazy(() => import('./Files'));
const ForgotPassword = lazy(() => import('./ForgotPassword'));
const LLPNCustomerCards = lazy(() => import('./LLPNCustomerCards'));
const LLPNAliases = lazy(() => import('./LLPNAliases'));
const LLPNInboundEmails = lazy(() => import('./LLPNInboundEmails'));
const LLPNOcrJobs = lazy(() => import('./LLPNOcrJobs'));
const Login = lazy(() => import('./Login'));
const Message = lazy(() => import('./Message'));
const Messages = lazy(() => import('./Messages'));
const NewMessage = lazy(() => import('./NewMessage'));
const PP = lazy(() => import('./PP'));
const Register = lazy(() => import('./Register'));
const RegisterCustomItem = lazy(() => import('./RegisterCustomItem'));
const RegisterItem = lazy(() => import('./RegisterItem'));
const ResultPage = lazy(() => import('./ResultPage'));
const RoadMap = lazy(() => import('./RoadMap'));
const Settings = lazy(() => import('./Settings'));
const Shop = lazy(() => import('./Shop'));
const SKUCard = lazy(() => import('./SKUCard'));
const TagTemplateEdit = lazy(() => import('./TagTemplateEdit'));
const TagTemplates = lazy(() => import('./TagTemplates'));
const TOC = lazy(() => import('./TOC'));
const WheelCampaignEdit = lazy(() => import('./WheelCampaignEdit'));
const WheelCampaigns = lazy(() => import('./WheelCampaigns'));

console.log(`Frontend is running on port: ${config.frontendPort}`);
console.log(`Backend is expected to run on port: ${config.backendPort}`);

function detectBrowserLanguage() {
  const navLang = navigator.language || navigator.userLanguage || 'en';
  const shortLang = navLang.split('-')[0].toLowerCase();
  const supported = ['cs', 'en', 'de', 'fr', 'es'];
  return supported.includes(shortLang) ? shortLang : 'en';
}

function isLocalFrontendHost() {
  const host = window.location.hostname;
  return host === 'localhost' || host === '127.0.0.1' || host === '::1';
}

function App() {
  const [isLoggedIn, setIsLoggedIn] = useState(false); // State to track login status
  const [theme, setTheme] = useState('dark'); // Default theme
  const [language, setLanguage] = useState(() => {
    const stored = llStorage.getItem('language');
    if (stored) return stored;
    const detected = detectBrowserLanguage();
    llStorage.setItem('language', detected);
    return detected;
  });
  const [testMode, setTestMode] = useState(() => !isLocalFrontendHost());

  // Fetch LostLink setup (testMode) from backend
  useEffect(() => {
    if (isLocalFrontendHost()) {
      setTestMode(false);
      return;
    }

    const fetchSetup = async () => {
      try {
        const res = await axiosInstance.get('/setup');
        setTestMode(!!res.data.testMode);
      } catch (err) {
        console.error('Failed to fetch setup:', err);
      }
    };
    fetchSetup();
  }, []);

  useEffect(() => {
    scheduleAutoLogout();
    // Load theme from localStorage or set default based on time
    const savedTheme = llStorage.getItem('theme');
    if (savedTheme) {
      setTheme(savedTheme);
      applyTheme(savedTheme);
    } else {
      const hour = new Date().getHours();
      const defaultTheme = hour >= 6 && hour <= 18 ? 'light' : 'dark';
      setTheme(defaultTheme);
      applyTheme(defaultTheme);
    }

    // Check if the user is logged in by verifying the token in localStorage
    const token = llStorage.getItem('token');
    setIsLoggedIn(!!token); // Set isLoggedIn to true if the token exists
  }, []);

  const applyTheme = (mode) => {
    const body = document.body;
    const heading = document.getElementById('main-heading');
    const paragraphs = document.querySelectorAll('.tab-content p');
    const heads = document.querySelectorAll('.tab-content h2');
    const appHeadingColor = mode === 'light' ? '#23497C' : '#f0e6d2';
    const appTextColor = mode === 'light' ? '#1a1a1a' : '#fff';

    document.documentElement.style.setProperty('--app-heading-color', appHeadingColor);
    document.documentElement.style.setProperty('--app-text-color', appTextColor);

    if (mode === 'light') {
      body.style.background = '#f5f5f5';
      body.style.color = appTextColor;
      if (heading) heading.style.color = appHeadingColor;
      paragraphs.forEach((p) => (p.style.color = appHeadingColor));
      heads.forEach((h) => (h.style.color = appHeadingColor));
    } else {
      body.style.background = '#0B1E3A';
      body.style.color = appTextColor;
      if (heading) heading.style.color = appHeadingColor;
      paragraphs.forEach((p) => (p.style.color = appHeadingColor));
      heads.forEach((h) => (h.style.color = appHeadingColor));
    }
  };

  const toggleTheme = () => {
    const newTheme = theme === 'dark' ? 'light' : 'dark';
    setTheme(newTheme);
    llStorage.setItem('theme', newTheme);
    applyTheme(newTheme);
    // Notify other components in the page about the theme change
    try {
      window.dispatchEvent(new CustomEvent('ll:themeChange', { detail: newTheme }));
    } catch (e) {
      // ignore for older browsers
    }
  };

  return (
    <Router>
      <div className="app-container"> {/* Added enclosing div */}
        <Header
          isLoggedIn={isLoggedIn}
          setIsLoggedIn={setIsLoggedIn}
          language={language}
          setLanguage={setLanguage}
          testMode={testMode}
        /> {/* Header is now global */}
        <div className="app-routes-container"> {/* Added enclosing div for Routes */}
          <Suspense fallback={<div className="route-loading">Loading...</div>}>
          {testMode && !isLoggedIn ? (
            <Routes>
              <Route
                path="/"
                element={
                  <div style={{ display: 'flex', minHeight: '60vh', alignItems: 'center', justifyContent: 'center', flexDirection: 'column' }}>
                  {
                    <h2>{t('comingSoon.title', language) || 'Coming soon'}</h2>
                    }
                    <p style={{ marginTop: 12 }}>{t('comingSoon.message', language) || 'Our site is in test mode.'}</p>
                    {
                    <a href="https://lostlink.store" style={{ marginTop: 16 }}>{t('comingSoon.emailSubscribeLink', language) || 'Login'}</a>
                    }
                  </div>
                }
              />
              <Route path="/login" element={<Login setIsLoggedIn={setIsLoggedIn} language={language} testMode={testMode} />} />
              {/* registration route still available but Register component will hide form when testMode is true */}
              <Route path="/register" element={<Register isLoggedIn={isLoggedIn} language={language} testMode={testMode} />} />
              <Route path="/:sn" element={<ResultPage isLoggedIn={isLoggedIn} language={language} />} />
              <Route path="/newmessage" element={<NewMessage isLoggedIn={isLoggedIn} language={language} />} />
              <Route path="/forgot-password" element={<ForgotPassword isLoggedIn={isLoggedIn} language={language} />} /> {/* New ForgotPassword route */}
            </Routes>
          ) : (
            <Routes>
              <Route path="/" element={<ResultPage language={language} />} />
              <Route path="/dashboard" element={<Dashboard isLoggedIn={isLoggedIn} language={language} />} />
              <Route path="/dashboard/items" element={<Dashboard isLoggedIn={isLoggedIn} language={language} />} />
              <Route path="/dashboard/items/:filter" element={<Dashboard isLoggedIn={isLoggedIn} language={language} />} />
              <Route path="/dashboard/others-items" element={<Dashboard isLoggedIn={isLoggedIn} language={language} />} />
              <Route path="/dashboard/others-items/:filter" element={<Dashboard isLoggedIn={isLoggedIn} language={language} />} />
              <Route path="/items/edit/:id" element={<EditItem isLoggedIn={isLoggedIn} language={language} />} />
              <Route path="/items/register" element={<RegisterItem isLoggedIn={isLoggedIn} language={language} />} />
              <Route path="/items/create" element={<RegisterCustomItem isLoggedIn={isLoggedIn} language={language} />} />
              <Route path="/login" element={<Login setIsLoggedIn={setIsLoggedIn} language={language} testMode={testMode} />} />
              <Route path="/register" element={<Register setIsLoggedIn={setIsLoggedIn} language={language} testMode={testMode} />} />            
              <Route path="/shop" element={<Shop isLoggedIn={isLoggedIn} language={language} />} />
              <Route path="/:sn" element={<ResultPage isLoggedIn={isLoggedIn} language={language} />} />
              <Route path="/settings" element={<Settings isLoggedIn={isLoggedIn} language={language} />} /> {/* New Settings route */}
              <Route path="/wheelcampaigns" element={<WheelCampaigns isLoggedIn={isLoggedIn} language={language} />} />
              <Route path="/wheelcampaigns/new" element={<WheelCampaignEdit language={language} />} />
              <Route path="/wheelcampaigns/edit/:id" element={<WheelCampaignEdit language={language} />} />
              <Route path="/llpn-customer-cards" element={<LLPNCustomerCards language={language} />} />
              <Route path="/llpn-aliases" element={<LLPNAliases language={language} />} />
              <Route path="/llpn-emails" element={<LLPNInboundEmails language={language} />} />
              <Route path="/llpn-ocr-jobs" element={<LLPNOcrJobs language={language} />} />
              <Route path="/files" element={<Files language={language} />} />
              <Route path="/api-keys" element={<ApiKeys language={language} />} />
              <Route path="/api-keys/new" element={<ApiKeyCard language={language} />} />
              <Route path="/api-keys/:apiKeyId" element={<ApiKeyCard language={language} />} />
              <Route path="/skus" element={<AdminSkus language={language} />} />
              <Route path="/skus/new" element={<SKUCard language={language} />} />
              <Route path="/skus/:id" element={<SKUCard language={language} />} />
              <Route path="/tag-templates" element={<TagTemplates language={language} />} />
              <Route path="/tag-templates/new" element={<TagTemplateEdit language={language} />} />
              <Route path="/tag-templates/:id/edit" element={<TagTemplateEdit language={language} />} />
              <Route path="/forgot-password" element={<ForgotPassword isLoggedIn={isLoggedIn} language={language} />} /> {/* New ForgotPassword route */}
              <Route path="/toc" element={<TOC isLoggedIn={isLoggedIn} />} language={language} />
              <Route path="/pp" element={<PP isLoggedIn={isLoggedIn} />} language={language} />
              <Route path="/message" element={<Message isLoggedIn={isLoggedIn} />} language={language} />
              <Route path="/newmessage" element={<NewMessage isLoggedIn={isLoggedIn} language={language} />} />
              <Route path="/messages" element={<Messages isLoggedIn={isLoggedIn} language={language} />} />            
              <Route path="/about" element={<About isLoggedIn={isLoggedIn} language={language} />} />
              <Route path="/roadmap" element={<RoadMap isLoggedIn={isLoggedIn} language={language} />} />            
            </Routes>
          )}
          </Suspense>
        </div>      
        <Footer language={language} testMode={testMode} />  
      </div>
      {
      <button id="themeToggle" className="theme-toggle-btn" onClick={toggleTheme}>
          {theme === 'dark' ? '☀️' : '🌙'}
      </button>
      }
  
    </Router>
  );
}

export default App;
